import { useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  BookMarked,
  BookOpen,
  CalendarClock,
  FileEdit,
  Heart,
  Leaf,
  LogOut,
  Mail,
  MailCheck,
  Menu,
  Plug,
  ScrollText,
  UserCog,
  Users,
  X,
  Youtube,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: BarChart3, end: true, perm: "analytics" },
  { to: "/admin/team", label: "Team & Roles", icon: UserCog, perm: "admin_users" },
  { to: "/admin/members", label: "Members", icon: Users, perm: "members_view" },
  { to: "/admin/contacts", label: "Email & Contacts", icon: Mail, perm: "subscribers_view" },
  { to: "/admin/assessment", label: "Assessment Content", icon: BookOpen, perm: "assessment_admin" },
  { to: "/admin/consultations", label: "Consultations", icon: CalendarClock, perm: "consultations" },
  { to: "/admin/messaging", label: "Messaging", icon: MailCheck, perm: "communications" },
  { to: "/admin/terrain", label: "Terrain and Truth", icon: Youtube, perm: "resources" },
  { to: "/admin/ebook", label: "Ebook Manager", icon: BookMarked, perm: "resources" },
  { to: "/admin/favorites", label: "Affiliate Resources", icon: Heart, perm: "resources" },
  { to: "/admin/content", label: "Website Content", icon: FileEdit, perm: "content" },
  { to: "/admin/integrations", label: "Integrations", icon: Plug, perm: "integrations" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, perm: "audit_view" },
];

const canAccess = (member, perm) => {
  const perms = member?.permissions || [];
  return perms.includes("*") || perms.includes(perm);
};

export default function AdminLayout() {
  const { member, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  if (member === null) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ivory pt-[76px]">
        <Leaf className="h-8 w-8 animate-pulse text-forest" />
      </main>
    );
  }
  const perms = member?.permissions || [];
  const isStaff = perms.includes("*") || perms.length > 0;
  if (!member || !isStaff) {
    return <Navigate to="/" state={{ from: location.pathname }} replace />;
  }
  const visibleNav = NAV.filter((item) => canAccess(member, item.perm));

  return (
    <main data-testid="admin-layout" className="min-h-screen bg-ivory pt-[76px]">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-8 px-5 py-10 sm:px-8 lg:flex-row">
        <div className="lg:hidden">
          <button
            data-testid="admin-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border border-forest/20 px-5 py-2.5 text-sm font-semibold text-forest"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            Admin Menu
          </button>
        </div>
        <aside
          className={`${open ? "block" : "hidden"} w-full shrink-0 lg:block lg:w-60`}
          aria-label="Admin navigation"
        >
          <div className="rounded-[2rem] border border-forest/10 bg-white/75 p-5 shadow-[0_16px_40px_rgba(23,63,53,0.08)] lg:sticky lg:top-24">
            <div className="flex items-center gap-2.5 px-3 pb-3">
              <img
                src="/images/rooted-healing-logo.png"
                alt="Rooted Healing emblem"
                className="h-8 w-8"
                width="1254"
                height="1254"
              />
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                Admin Center
              </p>
            </div>
            <nav className="flex flex-col gap-1">
              {visibleNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                      isActive ? "bg-forest text-ivory" : "text-charcoal/70 hover:bg-sage"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <p className="mt-5 border-t border-forest/10 px-3 pt-4 text-[11px] leading-relaxed text-taupe">
              Signed in as {member.email}. Every sensitive view is recorded in the audit log.
            </p>
            <button
              data-testid="admin-logout-button"
              onClick={async () => {
                await logout();
                navigate("/", { replace: true });
              }}
              className="mt-3 inline-flex w-full items-center gap-2 rounded-xl border border-forest/15 px-3.5 py-2.5 text-xs font-semibold text-charcoal/70 transition-colors hover:border-gold hover:text-forest"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out of the Admin Center
            </button>
          </div>
        </aside>
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </main>
  );
}
